import { type ConversationTextVariant } from '../../components/ConversationText'
import {
  type CatalogItemTagIcon,
  type CatalogItemTagVariant,
} from '../../value-objects/CatalogItemTag'
import { type ConversationTelemetryEvent } from '../../value-objects/ConversationTelemetryEvent'
import {
  type ConversationCartAction,
  type ConversationCatalogItemDelivery,
  type ConversationCatalogItemVendorBadge,
  type ConversationComponent,
  type ConversationEnvelopeItem,
  type ConversationMessageSender,
  type ConversationVendor,
} from '../../value-objects/ConversationTurnItem'
import {
  type CatalogItemVendorBadgeSlotEnum,
  CatalogItemVendorBadgeSlots,
  type ConversationCartActionIconEnum,
  ConversationCartActionIcons,
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../../value-objects/enums'
import {
  type ConversationCartActionApiModel,
  type ConversationCatalogItemDeliveryApiModel,
  type ConversationCatalogItemVendorBadgeApiModel,
  type ConversationComponentApiModel,
  type ConversationEnvelopeApiModel,
  type ConversationStreamEventApiModel,
  type ConversationVendorApiModel,
} from '../api-models/ConversationEventApiModel'

const normalizeSender = (sender: string): ConversationMessageSender =>
  sender === 'user' ? 'user' : 'llm'

const normalizeImg = (img?: string): string | undefined =>
  img && !img.includes('image_placeholder') ? img : undefined

const DEFAULT_RATING_SCALE_MAX = 5

const isCatalogItemVendorBadgeSlot = (
  slot: string
): slot is CatalogItemVendorBadgeSlotEnum =>
  (Object.values(CatalogItemVendorBadgeSlots) as string[]).includes(slot)

const isConversationCartActionIcon = (
  icon: string
): icon is ConversationCartActionIconEnum =>
  (Object.values(ConversationCartActionIcons) as string[]).includes(icon)

const mapVendorBadges = (
  badges: ConversationCatalogItemVendorBadgeApiModel[] = []
): ConversationCatalogItemVendorBadge[] =>
  badges
    .filter((badge) => isCatalogItemVendorBadgeSlot(badge.slot))
    .map((badge) => ({
      slot: badge.slot as CatalogItemVendorBadgeSlotEnum,
      text: badge.text,
      ...(badge.icon && { icon: badge.icon as CatalogItemTagIcon }),
      ...(badge.logo_url && { logoUrl: badge.logo_url }),
    }))

const withDealBadge = (
  badges: ConversationCatalogItemVendorBadge[],
  dealTag?: string
): ConversationCatalogItemVendorBadge[] => {
  if (!dealTag) return badges

  return [
    ...badges.filter(
      (badge) => badge.slot !== CatalogItemVendorBadgeSlots.DEAL
    ),
    { slot: CatalogItemVendorBadgeSlots.DEAL, text: dealTag },
  ]
}

const mapDelivery = (
  delivery?: ConversationCatalogItemDeliveryApiModel,
  detail?: { price?: string; date?: string }
): Nullable<ConversationCatalogItemDelivery> => {
  const type = detail?.price ?? ''
  const date = detail?.date ?? ''
  if (type && date) {
    return { label: `${type} ${date}`, type, date }
  }
  return delivery?.label ? { label: delivery.label } : null
}

const mapVendor = (
  vendor?: ConversationVendorApiModel
): Nullable<ConversationVendor> =>
  vendor
    ? {
        id: vendor.id,
        name: vendor.name,
        ...(vendor.logo_url && { logoUrl: vendor.logo_url }),
      }
    : null

const mapCartAction = (
  action?: ConversationCartActionApiModel
): Nullable<ConversationCartAction> => {
  if (!action?.text) return null

  const icon = action.icon
  return {
    text: action.text,
    ...(icon && isConversationCartActionIcon(icon) && { icon }),
  }
}

const TEXT_VARIANTS: readonly ConversationTextVariant[] = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
]

const normalizeTextVariant = (variant?: string): ConversationTextVariant =>
  (TEXT_VARIANTS as readonly string[]).includes(variant ?? '')
    ? (variant as ConversationTextVariant)
    : 'h4'

function mapConversationComponent(
  component: ConversationComponentApiModel
): Nullable<ConversationComponent> {
  switch (component.type) {
    case ConversationComponentTypes.MESSAGE:
      return {
        type: ConversationComponentTypes.MESSAGE,
        sender: normalizeSender(component.params.sender),
        content: component.params.content,
        ...(component.params.tags && { tags: component.params.tags }),
      }
    case ConversationComponentTypes.CONFIRMATION_OPTIONS:
      return {
        type: ConversationComponentTypes.CONFIRMATION_OPTIONS,
        options: component.params.options,
      }
    case ConversationComponentTypes.GROUPED_DROPDOWN:
      return {
        type: ConversationComponentTypes.GROUPED_DROPDOWN,
        groups: component.params.groups.map((group) => ({
          id: group.id,
          name: group.name,
          options: group.options.map((option) => ({
            id: option.id,
            name: option.name,
          })),
        })),
        groupLabel: component.params.group_label,
        optionLabel: component.params.option_label,
        defaultGroupId: component.params.default_group_id ?? null,
        defaultOptionId: component.params.default_option_id ?? null,
      }
    case ConversationComponentTypes.CART: {
      const cartVendor = mapVendor(component.params.vendor)
      const continueAction = mapCartAction(component.params.continue_action)

      return {
        type: ConversationComponentTypes.CART,
        cartId: component.params.cart_id,
        version: component.params.version,
        totalItems: component.params.total_items,
        total: component.params.estimated_total.price,
        currency: component.params.estimated_total.currency,
        items: component.params.items.map((item) => {
          const img = normalizeImg(item.img)
          return {
            id: item.line_id,
            name: item.title,
            ...(img && { img }),
            quantity: item.number_of_items,
            price: item.price.price,
          }
        }),
        ...(cartVendor && { vendor: cartVendor }),
        ...(component.params.total_label && {
          totalLabel: component.params.total_label,
        }),
        ...(continueAction && { continueAction }),
        ...(component.params.disclaimer && {
          disclaimer: component.params.disclaimer,
        }),
      }
    }
    case ConversationComponentTypes.CATALOG_ITEM: {
      const { title, body, price, tags, rating, delivery } = component.params
      const vendor = mapVendor(component.params.vendor)
      const img = normalizeImg(component.params.img)
      const moreInformation = component.params.more_information
      const vendorBadges = withDealBadge(
        mapVendorBadges(component.params.vendor_badges),
        moreInformation?.price?.deal_tag
      )
      const mappedDelivery = mapDelivery(delivery, moreInformation?.delivery)
      const listPrice = moreInformation?.price?.old_price?.price ?? 0
      const specification = moreInformation?.specification ?? []
      const sku =
        specification.find(([label]) => label.toLowerCase() === 'sku')?.[1] ??
        ''

      return {
        type: ConversationComponentTypes.CATALOG_ITEM,
        itemId: sku,
        ...(img && { img }),
        title,
        body,
        sku,
        price: price.price,
        currency: price.currency,
        tags: tags.map((tag) => ({
          ...(tag.icon && { icon: tag.icon as CatalogItemTagIcon }),
          text: tag.text,
          ...(tag.variant && {
            variant: tag.variant as CatalogItemTagVariant,
          }),
        })),
        agentAnalysis: moreInformation?.agent_analysis?.body ?? '',
        catalogDetails: specification.map(([label, value]) => ({
          label,
          value,
        })),
        ...(moreInformation?.quantity && {
          quantity: moreInformation.quantity,
        }),
        ...(vendor && { vendor }),
        ...(rating && {
          rating: {
            value: rating.value,
            count: rating.count,
            scaleMax: rating.scale_max || DEFAULT_RATING_SCALE_MAX,
          },
        }),
        ...(vendorBadges.length > 0 && { vendorBadges }),
        ...(mappedDelivery && { delivery: mappedDelivery }),
        ...(listPrice > 0 && { listPrice }),
      }
    }
    case ConversationComponentTypes.TEXT:
      return {
        type: ConversationComponentTypes.TEXT,
        variant: normalizeTextVariant(component.params.variant),
        content: component.params.text ?? component.params.content ?? '',
      }
    default:
      return null
  }
}

export const mapConversationEnvelopeEvent = (
  envelope: ConversationEnvelopeApiModel
): Omit<ConversationEnvelopeItem, 'id'> => ({
  kind: ConversationItemKinds.ENVELOPE,
  envelopeId: envelope.id,
  components: envelope.components
    .map(mapConversationComponent)
    .filter(
      (component): component is ConversationComponent => component !== null
    ),
  isAnswered: false,
})

export const mapConversationTelemetryEvent = (
  event: ConversationStreamEventApiModel
): ConversationTelemetryEvent => ({
  type: event.type,
  data: event.data,
  timestamp: event.timestamp ?? null,
})
